import * as React from "react";
import { useEffect, useState } from "react";
import { Modal, Button, Input, Select, DatePicker, Alert, Form, Row, Col } from "antd";
import { toast } from "react-toastify";

import PhoneInput from "../PhoneInput";
import Place from "../../types/Place";
import { fetchPlaces } from "../../api/places";
import MediatorRequest from "../../types/MediatorRequest";
import { convertStringToDBFormat } from "../../util/date";
import { createMediator } from "../../api/mediator";
import { convertPhoneNumber } from "../../util/common";
import { initialMediatorState } from "./states";
import { useTexts } from "../../hooks/useTexts";

const AddMediator = ({ successCallback }: { successCallback: () => void }) => {
    const { t } = useTexts();
    const [places, setPlaces] = useState<Place[]>([]);
    const [open, setOpen] = useState(false);
    const [mediator, setMediator] = useState<any>(initialMediatorState);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const openModal = () => setOpen(true);
    const closeModal = () => {
        setOpen(false);
        setErrors([]);
        form.resetFields();
        setMediator(initialMediatorState);
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setMediator({ ...mediator, [e.target.name]: e.target.value });
    };

    const handleSubmit = (): void => {
        setErrors([]);
        setLoading(true);
        const placeId: number = Number(mediator.place);
        const placeRequest: MediatorRequest = {
            firstname: mediator.firstname,
            lastname: mediator.lastname,
            phoneNumber: convertPhoneNumber(mediator.phoneNumber),
            birthDate: convertStringToDBFormat(mediator.birthDate),
            placeId
        };
        createMediator(placeRequest)
            .then(response => {
                if (response?.id) {
                    closeModal();
                    toast.success(t('mediator', 'mediatorAdded'), { theme: "colored" });
                    successCallback();
                } else if (!!response?.messages?.length && response?.messages?.length > 0) {
                    setErrors(response.messages);
                }
            })
            .catch(error => console.error("ERROR", error))
            .finally(() => {
                setLoading(false);
            });
    };

    const getPlaces = () => {
        fetchPlaces().then(response => {
            setPlaces(response?.content || []);
        }).catch(error => console.error("ERROR", error));
    };

    useEffect(() => {
        getPlaces();
    }, []);

    // Form value sync to local state
    const handleValuesChange = (changedValues: any, allValues: any) => {
        if (changedValues.birthDate) {
            setMediator({ ...mediator, birthDate: changedValues.birthDate });
        } else if (changedValues.place) {
            setMediator({ ...mediator, place: changedValues.place });
        }
    };

    return <>
        <Modal
            title={t('mediator', 'addMediator')}
            open={open}
            onCancel={closeModal}
            width={768}
            footer={null}
        >
            {errors?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                    {errors.map((err, idx) => (
                        <Alert key={idx} message={err} type="error" showIcon style={{ marginBottom: '8px' }} />
                    ))}
                </div>
            )}

            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleValuesChange}
                onFinish={handleSubmit}
            >
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item label={t('user', 'firstname')} required>
                            <Input
                                name="firstname"
                                value={mediator.firstname}
                                onChange={handleInput}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label={t('user', 'lastname')} required>
                            <Input
                                name="lastname"
                                value={mediator.lastname}
                                onChange={handleInput}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16} align="middle">
                    <Col xs={24} md={12}>
                        <Form.Item label={t('mediator', 'phone')} required>
                            <PhoneInput
                                error={!mediator.phoneNumber}
                                value={mediator.phoneNumber as string}
                                onChange={value => setMediator({ ...mediator, phoneNumber: value })}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label={t('mediator', 'birthDate')} name="birthDate" required>
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item label={t('mediator', 'placeObject')} name="place" required>
                            <Select
                                placeholder={t('mediator', 'placeObject')}
                                allowClear
                                showSearch
                                options={places.map((option: Place) => ({
                                    label: `${option.name} (${option.city})`,
                                    value: option.id
                                }))}
                                filterOption={(input, option) =>
                                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                    <Button onClick={closeModal}>
                        {t('common', 'close')}
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        disabled={!mediator.firstname || !mediator.lastname || !mediator.phoneNumber || !mediator.birthDate || !mediator.place}
                    >
                        {t('mediator', 'addMediator')}
                    </Button>
                </div>
            </Form>
        </Modal>

        <Button type="primary" onClick={openModal}>
            {t('mediator', 'addMediator')}
        </Button>
    </>;
};

export default AddMediator;